// Home-new (test) — cinematic hero opening.
//
// Sequence:
//   1. The hero video sits in its card as the background and plays its opening
//      scene (no scale, no fullscreen — it just stays put in the wrapper).
//   2. Shortly after the video starts, the navbar and header content
//      (label + h1 + paragraph + both CTAs) cascade in.
//
// KEY IMPLEMENTATION DETAILS (learned the hard way):
//   • `.container.is--hero_hp` ships from Webflow with `transform: scale(0.9)`
//     that the standard heroEntrance animates to 1. We skip heroEntrance, so we
//     neutralize it (`transform:none`) — otherwise the card renders at 90%.
//   • `.hp-hero_video-embed` ships at `transform: scale(1.2)` (heroEntrance's
//     start zoom). We skip heroEntrance, so clear it (`transform:none`) or the
//     video stays permanently zoomed 20%.
//   • The video is force-played + force-opacity:1 (guards against stale markup
//     and deferred autoplay).
//
// The reveal timeline starts on the video being ready (or a short fallback
// timer). Navbar + header must also be hidden pre-paint by the page-head FOUC
// guard — see HERO-EMBED.md.

export function initCinematicHero() {
  const section = document.querySelector('.section_hp-hero');
  if (!section) return;

  const embed = section.querySelector('.hp-hero_video-embed');
  const container = section.querySelector('.container.is--hero_hp');
  if (!embed || !container) return;

  const video = document.getElementById('hp-hero_video');
  const navbar = document.querySelector('.navbar-c');
  const hdr = section.querySelector('.hp-hero_header-warp');
  const label = section.querySelector('.hp_label');       // eyebrow
  const h1 = section.querySelector('.hp-hero_h1');
  const p = section.querySelector('.hp-hero_p2');
  const buttonsWrap = section.querySelector('.hp-hero_buttons-wrap');
  // The header content reveals as a staggered cascade — eyebrow → headline →
  // paragraph → CTAs — each element offset just after the one above it. The two
  // buttons animate via their WRAPPER so they still move as a single unit.
  const cascade = [label, h1, p, buttonsWrap].filter(Boolean);
  // Everything whose Webflow `transition: all` must be killed while GSAP writes
  // per-frame (restored at the end). The header WRAPPER is included so its own
  // container transition can't fight the reveal.
  const reveals = [navbar, hdr, ...cascade].filter(Boolean);

  // These elements carry a Webflow `transition: all`, which fights GSAP's
  // per-frame writes and makes the entrance jump. Kill it while we animate;
  // restore it when the timeline finishes.
  reveals.forEach((el) => { el.style.transition = 'none'; });

  // Remove the production hero's FOUC guard. It's injected at import time by
  // home/motion.js (which we import for acquire/section reveals) and hides
  // .main-cta / .button.is--secondary expecting the standard heroEntrance to
  // reveal them. We use the cinematic instead, so it would leave the buttons
  // stuck at opacity:0 — drop it and let our own reveal own visibility.
  document.getElementById('hp-hero-fouc-guard')?.remove();

  // Neutralize the Webflow scale(0.9) so the card is full-size.
  container.style.transform = 'none';

  // The embed ships at transform: scale(1.2) — heroEntrance's initial zoom. We
  // skip heroEntrance, so clear it or the video stays permanently zoomed 20%.
  embed.style.transform = 'none';

  // Always force the video visible + loading (stale opacity:0 / deferred autoplay).
  if (video) {
    video.style.opacity = '1';
    video.style.visibility = 'visible';
  }

  // Reduced motion — skip the cinematic, land on the final state. We set the
  // elements visible EXPLICITLY (autoAlpha:1) rather than clearProps, because the
  // head FOUC guard hides them via a stylesheet rule that clearProps wouldn't beat.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    if (navbar) gsap.set(navbar, { autoAlpha: 1, clearProps: 'transform' });
    gsap.set([hdr, ...cascade].filter(Boolean), { autoAlpha: 1, y: 0 });
    reveals.forEach((el) => { el.style.transition = ''; });
    return;
  }

  // ── Reveal choreography (tunable — best fine-tuned watching live) ────────────
  // Offsets are relative to the timeline start (video ready). The video plays in
  // place; the navbar fades in (opacity only, no movement), then the content
  // cascades up.
  const REVEAL = {
    y: 48,               // each content element rises from this far below (px)
    dur: 1.5,            // per-element rise duration (s) — slow, deliberate fade-up
    ease: 'power2.out',  // settle curve (softer tail than power3 over a long dur)
    navAt: 0.3,          // when the navbar starts (s after the video is ready)
    cascadeAt: 0.5,      // when the content cascade starts (s)
    step: 0.16,          // gap between each cascade element (s)
  };

  // Hide the reveal group (FOUC guard also hides them pre-paint). The navbar just
  // fades in (no movement); content elements rise from below, one after another.
  if (navbar) gsap.set(navbar, { autoAlpha: 0 });
  // The header WRAPPER only carried the FOUC opacity — clear it so its children
  // (which drive the cascade) can show; the children themselves start hidden below.
  if (hdr) gsap.set(hdr, { autoAlpha: 1, y: 0 });
  gsap.set(cascade, { autoAlpha: 0, y: REVEAL.y });

  // Force the inner buttons visible via INLINE styles — older FOUC guards may
  // still set `.main-cta { opacity:0 }` on the children, which the wrapper's
  // reveal can't override. Inline opacity/visibility beats those stylesheet rules.
  if (buttonsWrap) {
    gsap.set(buttonsWrap.querySelectorAll('.main-cta, .main-cta_text, .button, a, [class*="cta"]'), {
      opacity: 1,
      visibility: 'visible',
    });
  }

  let started = false;
  let fallbackTimer;
  const start = () => {
    if (started) return;
    started = true;
    clearTimeout(fallbackTimer);

    if (video && video.play) video.play().catch(() => {});

    const tl = gsap.timeline({
      onComplete: () => {
        // Restore Webflow's transitions for normal hover/scroll states.
        reveals.forEach((el) => { el.style.transition = ''; });
      },
    });

    // 1) Navbar fades in (opacity only — no movement).
    if (navbar) {
      tl.to(navbar, { autoAlpha: 1, duration: REVEAL.dur, ease: REVEAL.ease }, REVEAL.navAt);
    }

    // 2) Staggered content cascade — eyebrow → headline → paragraph → CTAs, each
    //    rising from below just after the one above it.
    cascade.forEach((el, i) => {
      tl.to(
        el,
        { autoAlpha: 1, y: 0, duration: REVEAL.dur, ease: REVEAL.ease },
        REVEAL.cascadeAt + i * REVEAL.step
      );
    });
  };

  // Start once the video can render its first frames, with a short fallback so we
  // never hang if the media events don't fire.
  if (video) {
    if (video.readyState >= 2) start();
    else {
      video.addEventListener('loadeddata', start, { once: true });
      video.addEventListener('canplay', start, { once: true });
      video.addEventListener('playing', start, { once: true });
    }
  }
  if (!started) fallbackTimer = window.setTimeout(start, 1000);
}
